import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { TableSkeleton } from "@/components/skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type AuditFeedEntry =
  inferRouterOutputs<AppRouter>["getAdminAuditFeed"][number];
type AuditFilters = {
  action?: string;
  actor?: string;
  from?: string;
  to?: string;
};

const DEFAULT_AUDIT_FILTERS: AuditFilters = {};

type AuditFilterField = {
  key: keyof AuditFilters;
  label: string;
  placeholder?: string;
  type?: "text" | "date";
};

const AUDIT_FILTER_FIELDS: ReadonlyArray<AuditFilterField> = [
  {
    key: "action",
    label: "Action",
    placeholder: "billing.subscription",
  },
  {
    key: "actor",
    label: "Actor",
    placeholder: "manager@example.com",
  },
  {
    key: "from",
    label: "From",
    type: "date",
  },
  {
    key: "to",
    label: "To",
    type: "date",
  },
] as const;

function formatAuditDate(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Just now";
  }
  return format(date, "d MMM yyyy, HH:mm");
}

function formatDetails(value: unknown) {
  if (!value) {
    return "No additional details.";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "Unserializable details";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function formatDetailKey(key: string) {
  return key
    .replaceAll("_", " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (character) => character.toUpperCase());
}

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed) && value.includes("T")) {
      return formatAuditDate(value);
    }

    return value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "—";
    }

    const preview = value.slice(0, 3).map((entry) => formatDetailValue(entry));
    return value.length > 3
      ? `${preview.join(", ")} +${value.length - 3} more`
      : preview.join(", ");
  }

  if (isRecord(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return "—";
    }

    const preview = entries.slice(0, 2).map(([key, nestedValue]) => {
      return `${formatDetailKey(key)}: ${formatDetailValue(nestedValue)}`;
    });

    return entries.length > 2
      ? `${preview.join(", ")} +${entries.length - 2} more`
      : preview.join(", ");
  }

  return formatDetails(value);
}

function getDetailRows(value: unknown) {
  if (typeof value === "string") {
    return [{ label: "Summary", value }];
  }

  if (!isRecord(value)) {
    return [{ label: "Details", value: formatDetailValue(value) }];
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    return [];
  }

  return entries.map(([key, entryValue]) => ({
    label: formatDetailKey(key),
    value: formatDetailValue(entryValue),
  }));
}

function buildAuditCsv(feed: AuditFeedEntry[]) {
  const rows = [
    [
      "createdAt",
      "source",
      "action",
      "actorLabel",
      "entityType",
      "entityId",
      "siteId",
      "details",
    ].join(","),
    ...feed.map((row) =>
      [
        row.createdAt,
        row.source,
        row.action,
        row.actorLabel,
        row.entityType,
        row.entityId ?? "",
        row.siteId ?? "",
        JSON.stringify(row.details ?? null),
      ]
        .map((value) => `"${String(value).replaceAll('"', '""')}"`)
        .join(",")
    ),
  ];

  return rows.join("\n");
}

function downloadAuditCsv(feed: AuditFeedEntry[], fileName: string) {
  const blob = new Blob([buildAuditCsv(feed)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function normalizeAuditFilters(filters: AuditFilters): AuditFilters {
  return {
    action: filters.action?.trim() || undefined,
    actor: filters.actor?.trim() || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}

function buildAuditFilterInput(filters: AuditFilters) {
  return {
    limit: 50,
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.actor ? { actor: filters.actor } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
  };
}

function areAuditFiltersEqual(left: AuditFilters, right: AuditFilters) {
  return (
    (left.action ?? "") === (right.action ?? "") &&
    (left.actor ?? "") === (right.actor ?? "") &&
    (left.from ?? "") === (right.from ?? "") &&
    (left.to ?? "") === (right.to ?? "")
  );
}

function countActiveFilters(filters: AuditFilters) {
  return Object.values(filters).filter(Boolean).length;
}

function hasInvalidDateRange(filters: AuditFilters) {
  if (!filters.from || !filters.to) {
    return false;
  }

  return filters.from > filters.to;
}

function formatEntityLabel(item: AuditFeedEntry) {
  const segments = [item.entityType];

  if (item.formName) {
    segments.push(item.formName);
  } else if (item.entityId) {
    segments.push(item.entityId);
  }

  if (item.siteId) {
    segments.push(`site ${item.siteId}`);
  }

  return segments.join(" · ");
}

export default function AdminAuditLog() {
  const [draftFilters, setDraftFilters] = useState<AuditFilters>(
    DEFAULT_AUDIT_FILTERS
  );
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>(
    DEFAULT_AUDIT_FILTERS
  );

  const filterInput = useMemo(
    () => buildAuditFilterInput(appliedFilters),
    [appliedFilters]
  );

  const auditQuery = useQuery({
    ...trpc.getAdminAuditFeed.queryOptions(filterInput),
    placeholderData: (previousData) => previousData,
  });

  const exportMutation = useMutation({
    ...trpc.exportAdminAuditFeed.mutationOptions(),
    onSuccess(data) {
      downloadAuditCsv(data.entries, data.fileName);
    },
  });

  const feed = auditQuery.data ?? [];
  const normalizedDraftFilters = useMemo(
    () => normalizeAuditFilters(draftFilters),
    [draftFilters]
  );
  const activeFilterCount = countActiveFilters(appliedFilters);
  const hasDraftChanges = !areAuditFiltersEqual(
    normalizedDraftFilters,
    appliedFilters
  );
  const invalidDateRange = hasInvalidDateRange(normalizedDraftFilters);
  const canClear =
    countActiveFilters(normalizedDraftFilters) > 0 || activeFilterCount > 0;

  const exportFilterInput = useMemo(
    () => normalizeAuditFilters(appliedFilters),
    [appliedFilters]
  );

  return (
    <AdminGuard>
      <PageContainer className="flex flex-col gap-6">
        <PageHeader
          title="Audit log"
          description="Trace high-impact changes across the platform."
        />

        <AuditFiltersCard
          filters={draftFilters}
          activeFilterCount={activeFilterCount}
          hasDraftChanges={hasDraftChanges}
          invalidDateRange={invalidDateRange}
          canClear={canClear}
          onFilterChange={(key, value) => {
            setDraftFilters((current) => ({
              ...current,
              [key]: value || undefined,
            }));
          }}
          onApply={() => setAppliedFilters(normalizedDraftFilters)}
          onClear={() => {
            setDraftFilters(DEFAULT_AUDIT_FILTERS);
            setAppliedFilters(DEFAULT_AUDIT_FILTERS);
          }}
        />

        <AuditFeedCard
          feed={feed}
          isLoading={auditQuery.isLoading}
          error={auditQuery.error}
          isExporting={exportMutation.isPending}
          onExport={() => exportMutation.mutate(exportFilterInput)}
        />
      </PageContainer>
    </AdminGuard>
  );
}

function AuditFiltersCard({
  filters,
  activeFilterCount,
  hasDraftChanges,
  invalidDateRange,
  canClear,
  onFilterChange,
  onApply,
  onClear,
}: {
  filters: AuditFilters;
  activeFilterCount: number;
  hasDraftChanges: boolean;
  invalidDateRange: boolean;
  canClear: boolean;
  onFilterChange: (key: keyof AuditFilters, value: string) => void;
  onApply: () => void;
  onClear: () => void;
}) {
  const helperText = invalidDateRange
    ? "The end date must be on or after the start date."
    : activeFilterCount > 0
      ? `${activeFilterCount} active filter${activeFilterCount === 1 ? "" : "s"}`
      : "Filter by action, actor, or date range.";

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-1">
          <CardTitle>Filters</CardTitle>
          <CardDescription
            className={invalidDateRange ? "text-destructive" : undefined}
          >
            {helperText}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!invalidDateRange) {
              onApply();
            }
          }}
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {AUDIT_FILTER_FIELDS.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={`audit-${field.key}`}>{field.label}</Label>
                <Input
                  id={`audit-${field.key}`}
                  type={field.type ?? "text"}
                  value={filters[field.key] ?? ""}
                  onChange={(event) =>
                    onFilterChange(field.key, event.target.value)
                  }
                  placeholder={field.placeholder}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {hasDraftChanges
                ? "You have unapplied filter changes."
                : "Showing the 50 most recent matching events. Export CSV to retrieve up to 500."}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="submit"
                className="min-w-36"
                disabled={invalidDateRange || !hasDraftChanges}
              >
                Apply filters
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClear}
                disabled={!canClear}
              >
                Clear
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AuditFeedCard({
  feed,
  isLoading,
  error,
  isExporting,
  onExport,
}: {
  feed: AuditFeedEntry[];
  isLoading: boolean;
  error: unknown;
  isExporting: boolean;
  onExport: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 pb-2 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <CardTitle>Recent events</CardTitle>
          <CardDescription>
            Promotions, subscription changes, site changes, sign-ins,
            certificates and form events.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          onClick={onExport}
          disabled={feed.length === 0 || isExporting}
        >
          {isExporting ? "Exporting…" : "Export CSV"}
        </Button>
      </CardHeader>
      <CardContent>
        {error && !feed.length ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load the audit feed.
          </div>
        ) : isLoading && !feed.length ? (
          <TableSkeleton columns={6} className="border-0" />
        ) : feed.length === 0 ? (
          <Empty className="border-dashed">
            <EmptyTitle>No audit events match the current filters</EmptyTitle>
            <EmptyDescription>
              Adjust the filters or clear them to review recent platform
              activity.
            </EmptyDescription>
          </Empty>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="text-right">Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feed.map((item) => (
                  <TableRow
                    key={`${item.source}:${item.id}`}
                    className="align-top"
                  >
                    <TableCell className="min-w-44">
                      <div className="space-y-2">
                        <span className="font-medium text-foreground">
                          {item.action}
                        </span>
                        {item.subjectUserId ? (
                          <p className="text-xs text-muted-foreground">
                            Subject {item.subjectUserId}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-40 text-muted-foreground">
                      {item.actorLabel}
                    </TableCell>
                    <TableCell className="min-w-24">
                      <Badge variant="outline">{item.source}</Badge>
                    </TableCell>
                    <TableCell className="min-w-56 text-muted-foreground">
                      {formatEntityLabel(item)}
                    </TableCell>
                    <TableCell className="min-w-72">
                      <AuditDetailsCell details={item.details} />
                    </TableCell>
                    <TableCell className="min-w-36 text-right text-muted-foreground">
                      {formatAuditDate(item.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AuditDetailsCell({ details }: { details: unknown }) {
  const rows = getDetailRows(details);

  if (rows.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">
        No additional details.
      </span>
    );
  }

  const visibleRows = rows.slice(0, 3);
  const hiddenRowCount = rows.length - visibleRows.length;

  return (
    <div className="space-y-2 rounded-md bg-muted/40 px-3 py-2 text-xs">
      {visibleRows.map((row) => (
        <div key={row.label} className="flex flex-col gap-1">
          <span className="font-medium text-foreground">{row.label}</span>
          <span className="break-words text-muted-foreground">{row.value}</span>
        </div>
      ))}
      {hiddenRowCount > 0 ? (
        <span className="block text-muted-foreground">
          +{hiddenRowCount} more detail{hiddenRowCount === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}
